import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getAuditLogs, getAuditSummary } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, MagnifyingGlass, ArrowClockwise, Warning, Info, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AuditLogPage() {
  const { isSpanish } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity_type: '', action: '', severity: '', skip: 0, limit: 50 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [filters.skip]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      if (filters.severity) params.severity = filters.severity;
      params.skip = filters.skip;
      params.limit = filters.limit;
      
      const [logsRes, summaryRes] = await Promise.all([
        getAuditLogs(params),
        getAuditSummary(7)
      ]);
      setLogs(logsRes.data.logs);
      setTotal(logsRes.data.total);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar bitacora' : 'Error loading audit log');
    } finally {
      setLoading(false);
    }
  };

  const severityBadge = (sev) => {
    const map = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return map[sev] || map.info;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="audit-log-page">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold text-[#F3F4F6]">
              <Shield className="w-8 h-8 inline mr-2 text-[#E2B05E]" />
              {isSpanish ? 'Bitacora de Auditoria' : 'Audit Log'}
            </h1>
            <p className="text-[#9CA3AF] mt-1">{total} {isSpanish ? 'eventos registrados' : 'events recorded'}</p>
          </div>
          <Button variant="outline" onClick={loadData} className="border-[#242E42] text-[#9CA3AF]">
            <ArrowClockwise className="w-4 h-4 mr-2" />
            {isSpanish ? 'Refrescar' : 'Refresh'}
          </Button>
        </div>

        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="valhalla-card p-4">
              <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Eventos (7 dias)' : 'Events (7 days)'}</p>
              <p className="text-2xl font-bold text-[#F3F4F6]">{summary.total_events}</p>
            </div>
            <div className="valhalla-card p-4">
              <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Eventos Criticos' : 'Critical Events'}</p>
              <p className="text-2xl font-bold text-red-400">{summary.critical_events}</p>
            </div>
            <div className="valhalla-card p-4">
              <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Tipos de Accion' : 'Action Types'}</p>
              <p className="text-2xl font-bold text-[#E2B05E]">{summary.by_action?.length || 0}</p>
            </div>
          </div>
        )}

        <div className="flex gap-3 flex-wrap">
          <Select value={filters.entity_type || 'all'} onValueChange={v => setFilters({...filters, entity_type: v === 'all' ? '' : v, skip: 0})}>
            <SelectTrigger className="w-44 valhalla-input"><SelectValue placeholder="Entidad" /></SelectTrigger>
            <SelectContent className="bg-[#151B26] border-[#242E42]">
              <SelectItem value="all">{isSpanish ? 'Todas las entidades' : 'All entities'}</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="employee">Employees</SelectItem>
              <SelectItem value="project">Projects</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="payment">Payments</SelectItem>
              <SelectItem value="vacation">Vacations</SelectItem>
              <SelectItem value="supplier">Suppliers</SelectItem>
              <SelectItem value="purchase_order">Purchase Orders</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.severity || 'all'} onValueChange={v => setFilters({...filters, severity: v === 'all' ? '' : v, skip: 0})}>
            <SelectTrigger className="w-36 valhalla-input"><SelectValue placeholder="Severidad" /></SelectTrigger>
            <SelectContent className="bg-[#151B26] border-[#242E42]">
              <SelectItem value="all">{isSpanish ? 'Todas' : 'All'}</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadData} className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14]">
            <MagnifyingGlass className="w-4 h-4 mr-2" />
            {isSpanish ? 'Filtrar' : 'Filter'}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E2B05E]"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="valhalla-card p-12 text-center">
            <Shield className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#F3F4F6] font-semibold">{isSpanish ? 'Sin eventos registrados' : 'No events recorded'}</p>
            <p className="text-[#9CA3AF] text-sm mt-1">{isSpanish ? 'Las acciones criticas se registraran aqui' : 'Critical actions will be logged here'}</p>
          </div>
        ) : (
          <div className="valhalla-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#242E42]">
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Fecha' : 'Date'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Accion' : 'Action'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Entidad' : 'Entity'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Actor' : 'Actor'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">IP</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Severidad' : 'Severity'}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log._id} className="border-b border-[#242E42]/50 hover:bg-[#1E2636]/50 transition-colors">
                      <td className="p-3 text-[#9CA3AF] text-sm whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString('es-ES') : 'N/A'}</td>
                      <td className="p-3 text-[#F3F4F6] font-medium text-sm">{log.action}</td>
                      <td className="p-3 text-[#9CA3AF] text-sm">{log.entity_type}/{log.entity_id?.slice(-6) || 'N/A'}</td>
                      <td className="p-3 text-[#9CA3AF] text-sm">{log.actor_email || 'System'}</td>
                      <td className="p-3 text-[#9CA3AF] text-sm font-mono">{log.ip_address || '-'}</td>
                      <td className="p-3"><Badge className={severityBadge(log.severity)}>{log.severity}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > filters.limit && (
              <div className="flex justify-between items-center p-4 border-t border-[#242E42]">
                <Button variant="outline" disabled={filters.skip === 0} onClick={() => setFilters({...filters, skip: Math.max(0, filters.skip - filters.limit)})} className="border-[#242E42] text-[#9CA3AF]">
                  {isSpanish ? 'Anterior' : 'Previous'}
                </Button>
                <span className="text-[#9CA3AF] text-sm">{filters.skip + 1}-{Math.min(filters.skip + filters.limit, total)} de {total}</span>
                <Button variant="outline" disabled={filters.skip + filters.limit >= total} onClick={() => setFilters({...filters, skip: filters.skip + filters.limit})} className="border-[#242E42] text-[#9CA3AF]">
                  {isSpanish ? 'Siguiente' : 'Next'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
